"use client"

import { useDateLocale } from "../../../lib/locale-context"
import { useTr } from "../../../lib/use-tr"
import { ArrowUpRight, Star } from "lucide-react"

import {
  type RatingRow,
  type RatingSummary,
  ratingSourceLabel,
} from "@/lib/apps/autoleads/lead-ratings"
import { isHttpUrl } from "@/lib/safe-url"

import { Badge } from "@/components/ui/badge"

import { LEADS_PANE_T } from "./translations"

// The outbound arrow closing a linked row: centred on the row, faint at
// rest, full ink on hover or keyboard focus (still visible on touch).
export const SIGNAL_LINK_ICON_CLASS =
  "text-muted-foreground/50 size-3.5 shrink-0 self-center transition-colors group-hover/signal:text-foreground group-focus-visible/signal:text-foreground"

// Ratings always read with one decimal ("4,0", "4.5"), chip and rows alike.
function formatRating(value: number, locale: string): string {
  return value.toLocaleString(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })
}

/**
 * Every rating of a lead in one chip: the review-weighted mean across sites,
 * with the review count (or the number of sites) beside it. Display only:
 * the rows live in the online presence section.
 */
export function LeadRatingChip({ summary }: { summary: RatingSummary }) {
  const tr = useTr(LEADS_PANE_T)
  const locale = useDateLocale()
  // No review anywhere: a "0,0 (0)" score would read as a bad rating.
  if (summary.reviewsCount === 0) {
    return (
      <Badge variant="outline" className="gap-1 tabular-nums">
        <Star className="size-3" />
        {tr("ratingNoReviews")}
      </Badge>
    )
  }
  const detail =
    summary.rows.length > 1
      ? tr("ratingSites").replace("{n}", String(summary.rows.length))
      : summary.reviewsCount !== null
        ? `(${summary.reviewsCount.toLocaleString(locale)})`
        : null
  return (
    <Badge variant="outline" className="gap-1 tabular-nums">
      <Star className="size-3" />
      {formatRating(summary.average, locale)}
      {detail && <span className="text-muted-foreground">{detail}</span>}
    </Badge>
  )
}

function RatingRowContent({ row, locale }: { row: RatingRow; locale: string }) {
  const tr = useTr(LEADS_PANE_T)
  return (
    <>
      <Star className="text-muted-foreground mt-0.5 size-3 shrink-0" />
      <span className="flex-1 font-medium">
        {row.source
          ? ratingSourceLabel(row.source)
          : tr("presenceRatingFallback")}
      </span>
      {/* A site without a review has no score worth reading, like the chip. */}
      {row.reviewsCount === 0 ? (
        <span className="text-muted-foreground">{tr("ratingNoReviews")}</span>
      ) : (
        <span className="tabular-nums">
          {formatRating(row.rating, locale)}
          {row.reviewsCount !== null && (
            <span className="text-muted-foreground">
              {" "}
              ({row.reviewsCount.toLocaleString(locale)})
            </span>
          )}
        </span>
      )}
    </>
  )
}

/** One row per rated site, linked to its page when known. */
export function LeadRatingDetails({ summary }: { summary: RatingSummary }) {
  const locale = useDateLocale()
  return (
    <ul>
      {summary.rows.map((row) => (
        <li key={row.source}>
          {isHttpUrl(row.sourceUrl) ? (
            <a
              href={row.sourceUrl}
              target="_blank"
              rel="noreferrer"
              className="group/signal hover:bg-accent focus-visible:ring-ring -ml-2 flex items-start gap-2 rounded-sm px-2 py-1.5 outline-none focus-visible:ring-2"
            >
              <RatingRowContent row={row} locale={locale} />
              <ArrowUpRight className={SIGNAL_LINK_ICON_CLASS} />
            </a>
          ) : (
            <div className="flex items-start gap-2 py-1.5">
              <RatingRowContent row={row} locale={locale} />
            </div>
          )}
        </li>
      ))}
    </ul>
  )
}
